import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isTestModeEnabled, setTestModeEnabled, subscribeTestMode } from '../../lib/testMode';

// ---------------------------------------------------------------------------
// Test Mode — React context (CLE-250).
//
// Thin React wrapper over the module-level test-mode state in lib/testMode.ts.
// Components read `enabled` and call `toggle()` / `setEnabled()`. On any change
// we clear the React Query cache so every page refetches — switching cleanly
// between real data and the simulated dataset without a page reload.
// ---------------------------------------------------------------------------

interface TestModeContextValue {
  enabled: boolean;
  toggle: () => void;
  setEnabled: (enabled: boolean) => void;
}

const TestModeContext = createContext<TestModeContextValue | undefined>(undefined);

export function TestModeProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabledState] = useState<boolean>(isTestModeEnabled);

  // Keep React state in sync if the module-level flag changes elsewhere — and
  // drop the cache then too: the flag flipping from the auth store (demo
  // login/logout) must never leave the other dataset's lists on screen.
  useEffect(
    () =>
      subscribeTestMode((next) => {
        setEnabledState(next);
        queryClient.clear();
      }),
    [queryClient],
  );

  // Whenever Test Mode flips, drop all cached query data so the next render
  // refetches through the (now switched) data source.
  const applyChange = useCallback(
    (next: boolean) => {
      setTestModeEnabled(next);
      queryClient.clear();
    },
    [queryClient],
  );

  const setEnabled = useCallback((value: boolean) => applyChange(value), [applyChange]);
  const toggle = useCallback(() => applyChange(!isTestModeEnabled()), [applyChange]);

  return (
    <TestModeContext.Provider value={{ enabled, toggle, setEnabled }}>
      {children}
    </TestModeContext.Provider>
  );
}

export function useTestMode(): TestModeContextValue {
  const ctx = useContext(TestModeContext);
  if (!ctx) {
    throw new Error('useTestMode must be used within a TestModeProvider');
  }
  return ctx;
}
